import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { currencyService, SUPPORTED_CURRENCIES } from '../../services/currencyService';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';

function UserSettings() {
  const { currentUser } = useAuth();
  const { currentLanguage, changeLanguage } = useLanguage();
  const { t } = useTranslation();
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadUserSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  async function loadUserSettings() {
    if (!currentUser?.uid) return;

    try {
      setLoading(true);
      const userCurrency = await currencyService.getUserCurrency(currentUser.uid);
      setSelectedCurrency(userCurrency);
    } catch (error) {
      setError(t('settings.settingsError') + ': ' + error.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveSettings() {
    if (!currentUser?.uid) return;

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const success = await currencyService.setUserCurrency(currentUser.uid, selectedCurrency);
      
      if (success) {
        setSuccess(t('settings.settingsSaved'));
      } else {
        setError(t('settings.settingsError'));
      }
    } catch (error) {
      setError(t('settings.settingsError') + ': ' + error.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 3 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography 
        variant="h3" 
        component="h1" 
        gutterBottom 
        sx={{ 
          textAlign: { xs: 'center', sm: 'left' },
          fontWeight: 700,
          background: 'linear-gradient(45deg, #d32f2f 30%, #f44336 90%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 4,
        }}
      >
        {t('settings.title')}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>{success}</Alert>}

      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          border: '1px solid rgba(211, 47, 47, 0.1)',
          background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
        }}
      >
        <Typography variant="h6" gutterBottom>
          {t('common.currency')} {t('common.preferences')}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('settings.currencyDescription')}
        </Typography>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>{t('common.currency')}</InputLabel>
          <Select
            value={selectedCurrency}
            label={t('common.currency')}
            onChange={(e) => setSelectedCurrency(e.target.value)}
          >
            {Object.entries(SUPPORTED_CURRENCIES).map(([code, currency]) => (
              <MenuItem key={code} value={code}>
                {currency.symbol} {currency.name} ({code})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
          {t('common.language')} {t('common.preferences')}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('settings.languageDescription')}
        </Typography>

        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel>{t('common.language')}</InputLabel>
          <Select
            value={currentLanguage}
            label={t('common.language')}
            onChange={(e) => changeLanguage(e.target.value)}
          >
            {[
              { code: 'en', name: 'English' },
              { code: 'th', name: 'ไทย' },
              { code: 'ja', name: '日本語' },
              { code: 'zh', name: '中文' }
            ].map((lang) => (
              <MenuItem key={lang.code} value={lang.code}>
                {lang.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSaveSettings}
            disabled={saving}
            sx={{
              borderRadius: 25,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '1rem',
            }}
          >
            {saving ? <CircularProgress size={20} /> : t('settings.saveSettings')}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
}

export default UserSettings; 